import type { ScenarioKeyNode, ScenarioNode, Tier, Tone } from '../shared/types';

/**
 * §10.1 — the customer line is resolved for the assigned persona first, then for
 * the tone the previous choice earned. Consequence is expressed through what the
 * prospect says and how they say it, never through a different path (§10.2).
 */
export function toneFor(previousTier: Tier | null): Tone {
  if (previousTier === null) return 'neutral';       // first node
  if (previousTier === 'best') return 'receptive';
  if (previousTier === 'good') return 'neutral';
  return 'hostile';                                   // weak or bad
}

export function customerLineFor(
  node: ScenarioNode,
  persona: string | null,
  tone: Tone
): string {
  const override = persona ? node.personaOverrides?.[persona] : undefined;
  if (override) return override;
  const variant = node.toneVariants?.[tone];
  if (variant) return variant;
  return node.customerLine ?? '';
}

/**
 * §6.10 — the read on whatever line actually rendered.
 *
 * Tagged per rendering rather than per node, and resolved by the same
 * precedence as `customerLineFor`, because a persona override replaces the line
 * outright: the same node asks the burned prospect a question and gives the
 * drowning one a statement about money. A rendering with no entry asked nothing,
 * and returns null rather than borrowing the base line's read.
 */
export function buyingSignalFor(
  node: ScenarioKeyNode,
  scenarioNode: ScenarioNode,
  persona: string | null,
  tone: Tone
): string | null {
  const map = node.buyingSignal;
  if (!map) return null;
  const override = persona ? scenarioNode.personaOverrides?.[persona] : undefined;
  if (override) return (persona && map[persona]) ?? null;
  if (scenarioNode.toneVariants?.[tone]) return null;
  return map.default ?? null;
}

/**
 * Which audio clip matches what is on screen, if one was generated (§16.1).
 *
 * The lookup mirrors `customerLineFor` exactly: whichever line won, its clip is
 * the one that plays. They resolve in the same order for the same reason — the
 * audio has to be the words, not an approximation of them.
 */
export function audioFor(node: ScenarioNode, persona: string | null, tone: Tone): string | null {
  if (!node.audio) return null;
  const keys = [persona ? `persona:${persona}` : null, persona, tone, 'neutral'];
  for (const key of keys) {
    if (key && node.audio[key]) return node.audio[key];
  }
  return null;
}

/**
 * §16.1 — the rep's own line, in the second voice, played back with the choice
 * they made rather than offered before it. Reading six options is faster than
 * listening to them; hearing the one you picked read aloud is what teaches
 * how it lands.
 *
 * The variant name is the one the render pipeline slugged, so a set key like
 * `persona:burned` is looked up as `opt_persona_burned_<optionId>`.
 */
export function optionAudioFor(
  node: ScenarioNode,
  optionSetKey: string | null,
  optionId: string | null
): string | null {
  if (!node.audio || !optionSetKey || !optionId) return null;
  const slug = (text: string) => text.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
  return node.audio[`opt_${slug(optionSetKey)}_${slug(optionId)}`] ?? null;
}
